import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import {
  PEOPLE_META,
  TEAM,
  ASSOCIATES,
  BEHIND,
  ARCHIVE,
  INDIA_MAP,
  isClosed,
} from "../lib/peopleData";

const Header = () => (
  <header className="fixed top-0 inset-x-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/10">
    <div className="mx-auto max-w-[1500px] w-full px-5 sm:px-8 h-16 flex items-center justify-between">
      <Link to="/" data-testid="site-logo" className="shrink-0">
        <img
          src="/owos-logo-badge.png"
          alt="One World of Sports"
          className="h-11 w-auto object-contain"
        />
      </Link>
      <Link
        to="/#gateways"
        data-testid="people-back-explore-top"
        className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-neutral-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
        Explore
      </Link>
    </div>
  </header>
);

const SectionLabel = ({ children, testid }) => (
  <p
    data-testid={testid}
    className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-[#e62429]"
  >
    <span className="h-px w-10 bg-[#e62429]" />
    {children}
  </p>
);

const Years = ({ years }) => {
  if (isClosed(years)) {
    const m = years.match(/^(.*?)(\d{4})\s*$/);
    return (
      <span className="shrink-0 tabular-nums text-sm tracking-wide text-neutral-400">
        {m[1]}
        <span className="text-[#e62429] font-semibold">{m[2]}</span>
      </span>
    );
  }
  return (
    <span className="shrink-0 tabular-nums text-sm tracking-wide text-neutral-400">
      {years}
    </span>
  );
};

const Reveal = ({ children, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function People() {
  useEffect(() => {
    document.title = "People — One World of Sports";
  }, []);

  return (
    <div data-testid="people-page" className="bg-[#0a0a0a] min-h-screen text-white">
      <Header />

      {/* 1. PEOPLE — a very simple opening moment */}
      <section className="mx-auto max-w-[1500px] px-5 sm:px-8 pt-36 sm:pt-48 pb-16 sm:pb-24">
        <Reveal>
          <SectionLabel testid="people-eyebrow">{PEOPLE_META.label}</SectionLabel>
          <h1 className="mt-6 font-display uppercase text-white text-[22vw] sm:text-[16vw] lg:text-[12rem] leading-[0.82]">
            {PEOPLE_META.title}
          </h1>
        </Reveal>
      </section>

      {/* 2. TEAM */}
      <section
        data-testid="people-team"
        className="mx-auto max-w-[1500px] px-5 sm:px-8 pb-20 sm:pb-28"
      >
        <Reveal>
          <h2 className="font-display uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-none border-b border-white/12 pb-6">
            Team
          </h2>
        </Reveal>
        <div className="mt-8 columns-1 sm:columns-2 lg:columns-3 gap-x-12">
          {TEAM.map((p, i) => (
            <div
              key={`${p.name}-${i}`}
              data-testid={`team-row-${i}`}
              className={`break-inside-avoid flex items-baseline justify-between gap-4 border-b border-white/[0.07] py-3.5`}
            >
              <span className="font-display uppercase text-xl sm:text-2xl leading-none tracking-tight">
                {p.name}
              </span>
              <Years years={p.years} />
            </div>
          ))}
        </div>
      </section>

      {/* 3. ARCHIVAL PHOTO BREAK — loose, accumulated documentary interruption */}
      <section
        data-testid="people-archive"
        aria-label="Archival photographs"
        className="relative border-y border-white/10 bg-[#0c0c0c] py-14 sm:py-20 overflow-hidden"
      >
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="columns-2 lg:columns-3 gap-4 sm:gap-6">
            {ARCHIVE.map((ph, i) => (
              <motion.figure
                key={ph.src}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.06, ease: [0.16, 1, 0.3, 1] }}
                className="mb-4 sm:mb-6 break-inside-avoid"
                style={{ transform: `rotate(${i % 2 === 0 ? -1.1 : 1.1}deg)` }}
              >
                <img
                  src={ph.src}
                  alt=""
                  loading="lazy"
                  className="w-full h-auto border border-white/10 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.8)]"
                />
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* 4. ASSOCIATES & PARTNERS — names, geography, years + supplied India map */}
      <section
        data-testid="people-associates"
        className="mx-auto max-w-[1500px] px-5 sm:px-8 py-20 sm:py-28"
      >
        <Reveal>
          <h2
            data-testid="associates-label"
            className="font-display uppercase text-white text-4xl sm:text-5xl leading-[0.95] border-b border-white/12 pb-6"
          >
            Associates &amp; Partners
          </h2>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-start">
          <div className="lg:col-span-7 order-2 lg:order-1">
            {ASSOCIATES.map((a, i) => (
              <div
                key={`${a.name}-${i}`}
                data-testid={`associate-row-${i}`}
                className="grid grid-cols-12 items-baseline gap-3 border-b border-white/[0.09] py-5"
              >
                <span className="col-span-12 sm:col-span-4 font-display uppercase text-2xl leading-none tracking-tight">
                  {a.name}
                </span>
                <span className="col-span-8 sm:col-span-5 text-[11px] sm:text-xs uppercase tracking-[0.2em] font-bold text-neutral-400">
                  {a.geo}
                </span>
                <span className="col-span-4 sm:col-span-3 text-right">
                  <Years years={a.years} />
                </span>
              </div>
            ))}
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2 lg:sticky lg:top-24">
            <div className="bg-neutral-50 p-5 sm:p-7 border border-white/10 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.9)] max-w-[440px] mx-auto">
              <img
                src={INDIA_MAP}
                alt="Oneworld operating reach across India"
                data-testid="india-map"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 5. BEHIND THE SCENE — specialists, expertise, years. No forced illustration. */}
      <section
        data-testid="people-behind"
        className="mx-auto max-w-[1500px] px-5 sm:px-8 pt-4 pb-24 sm:pb-32"
      >
        <Reveal>
          <h2
            data-testid="behind-label"
            className="font-display uppercase text-white text-4xl sm:text-5xl leading-[0.95] border-b border-white/12 pb-6"
          >
            Behind the Scene
          </h2>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-0">
          {BEHIND.map((b, i) => (
            <div
              key={`${b.name}-${i}`}
              data-testid={`behind-row-${i}`}
              className={`flex items-baseline justify-between gap-6 border-b border-white/[0.08] py-5`}
            >
              <span className="min-w-0">
                <span className="block font-display uppercase text-xl sm:text-2xl leading-tight tracking-tight">
                  {b.name}
                </span>
                <span className="mt-1 block text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500">
                  {b.area}
                </span>
              </span>
              <Years years={b.years} />
            </div>
          ))}
        </div>
      </section>

      {/* Route back to Explore */}
      <section className="border-t border-white/10 py-16">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 flex justify-center">
          <Link
            to="/#gateways"
            data-testid="people-back-explore-bottom"
            className="group inline-flex items-center gap-3 border border-white/20 px-8 py-4 text-xs uppercase tracking-[0.28em] font-bold text-white hover:bg-white hover:text-black transition-colors"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
            Back to Explore
            <ArrowUpRight className="h-4 w-4 opacity-40" />
          </Link>
        </div>
      </section>
    </div>
  );
}
